import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  enviarOrdenADropi,
  fetchDropiGeografia,
  fetchDropiHuella,
  fetchDropiPrevisualizacion,
  fetchDropiProducto,
  fetchDropiProductos,
  fetchDropiStatus,
  removeDropiConfig,
  saveDropiConfig,
  sincronizarOrdenDropi,
  sincronizarStockDropi,
  testDropiConnection,
} from '@/api/dropi';
import { queryKeys } from '@/lib/query-keys';
import type { SaveDropiPayload } from '@/types/dropi';

/**
 * Integración con Dropi (docs/DROPI.md).
 *
 * El estado y la geografía los lee cualquier miembro; guardar, probar, enviar la orden y
 * sincronizarla es solo ADMIN (el backend lo bloquea igual: esto es para no pintar botones que
 * no van a funcionar).
 *
 * Este archivo es la capa de datos de Dropi del panel, no solo la de la tarjeta de Integraciones:
 * Órdenes (`OrderDropiDialog`) y Productos (`ProductDropiDialog`) importan de acá sus hooks, así
 * que no se duplica la consulta ni la invalidación de la caché en cada pantalla.
 */

/** `GET .../dropi` — estado de la conexión. */
export function useDropiStatus(tenantId: string | null) {
  return useQuery({
    queryKey: queryKeys.dropi.status(tenantId ?? ''),
    queryFn: () => fetchDropiStatus(tenantId ?? ''),
    enabled: Boolean(tenantId),
  });
}

/** `PUT .../dropi` (ADMIN) — guarda token/origen/etiqueta (el backend hace merge). */
export function useSaveDropiConfig(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SaveDropiPayload) => saveDropiConfig(tenantId ?? '', payload),
    onSuccess: () => {
      if (tenantId) queryClient.invalidateQueries({ queryKey: queryKeys.dropi.status(tenantId) });
    },
  });
}

/** `DELETE .../dropi` (ADMIN) — desconecta y borra el token. */
export function useRemoveDropiConfig(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => removeDropiConfig(tenantId ?? ''),
    onSuccess: () => {
      if (tenantId) queryClient.invalidateQueries({ queryKey: queryKeys.dropi.status(tenantId) });
    },
  });
}

/** `POST .../dropi/test` (ADMIN). Siempre 200: el veredicto va en `ok`. */
export function useTestDropiConnection(tenantId: string | null) {
  return useMutation({
    mutationFn: (payload?: { token?: string; origin?: string }) =>
      testDropiConnection(tenantId ?? '', payload),
  });
}

/** Geografía oficial de Dropi (departamentos, y las ciudades del que se pida). */
export function useDropiGeografia(tenantId: string | null, departamento?: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.dropi.geografia(tenantId ?? '', departamento),
    queryFn: () => fetchDropiGeografia(tenantId ?? '', departamento),
    enabled: Boolean(tenantId) && enabled,
    // El catálogo cambia poco: se reusa lo ya cargado mientras la pantalla está abierta.
    staleTime: 30 * 60 * 1000,
  });
}

/** Catálogo de Dropi para elegir el producto a vincular. */
export function useDropiProductos(tenantId: string | null, startData = 0, enabled = true) {
  return useQuery({
    queryKey: queryKeys.dropi.productos(tenantId ?? '', startData),
    queryFn: () => fetchDropiProductos(tenantId ?? '', { startData, pageSize: 30 }),
    enabled: Boolean(tenantId) && enabled,
  });
}

/**
 * UN producto de Dropi por su id. Solo se dispara cuando el panel lo pide (`enabled`): el admin pega
 * el id que ya tiene y se trae ese producto de una sola llamada, sin recorrer el catálogo paginado.
 * Sin `retry`: si Dropi responde 404 (id que no existe) reintentar no cambia nada.
 */
export function useDropiProducto(
  tenantId: string | null,
  productId: number | null,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.dropi.producto(tenantId ?? '', productId ?? 0),
    queryFn: () => fetchDropiProducto(tenantId ?? '', productId ?? 0),
    enabled: Boolean(tenantId) && Boolean(productId) && enabled,
    retry: false,
  });
}

/**
 * Huella digital de un teléfono. Solo se dispara cuando el panel la pide (`enabled`), para no
 * gastar llamadas a Dropi por cada orden que se abre.
 */
export function useDropiHuella(tenantId: string | null, telefono: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.dropi.huella(tenantId ?? '', telefono ?? ''),
    queryFn: () => fetchDropiHuella(tenantId ?? '', telefono ?? ''),
    enabled: Boolean(tenantId) && Boolean(telefono) && enabled,
    retry: false,
  });
}

/** `GET .../dropi/previsualizacion` — qué se enviaría (lo pinta el diálogo de envío). */
export function useDropiPrevisualizacion(
  tenantId: string | null,
  orderId: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.dropi.previsualizacion(tenantId ?? '', orderId ?? ''),
    queryFn: () => fetchDropiPrevisualizacion(tenantId ?? '', orderId ?? ''),
    enabled: Boolean(tenantId) && Boolean(orderId) && enabled,
    retry: false,
  });
}

/** `POST .../orders/:orderId/dropi` (ADMIN) — crea la orden en Dropi (o la actualiza con `reenviar`). */
export function useEnviarOrdenADropi(tenantId: string | null, orderId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (opciones?: { reenviar?: boolean }) =>
      enviarOrdenADropi(tenantId ?? '', orderId ?? '', opciones),
    onSuccess: () => {
      if (!tenantId) return;
      // El envío deja `dropiOrderId`/`dropiEstado` en la orden: hay que refrescar el detalle.
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all(tenantId) });
    },
  });
}

/** `POST .../orders/:orderId/dropi/sincronizar` (ADMIN) — trae el estado actual desde Dropi. */
export function useSincronizarOrdenDropi(tenantId: string | null, orderId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => sincronizarOrdenDropi(tenantId ?? '', orderId ?? ''),
    onSuccess: () => {
      if (!tenantId) return;
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all(tenantId) });
    },
  });
}

/**
 * `POST .../dropi/stock/sincronizar` (ADMIN) — trae el stock REAL de Dropi de los productos
 * vinculados (docs/DROPI.md).
 *
 * Al terminar hay que refrescar el catálogo: el stock que devuelve se guarda en cada producto y es el
 * número con el que el bot decide si lo ofrece. El backend además lo corre solo cada
 * `DROPI_STOCK_SYNC_MINUTOS` minutos, así que este botón es el «ahora mismo».
 */
export function useSincronizarStockDropi(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => sincronizarStockDropi(tenantId ?? ''),
    onSuccess: () => {
      if (!tenantId) return;
      queryClient.invalidateQueries({ queryKey: queryKeys.products.all(tenantId) });
    },
  });
}
